import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { last } from 'lodash';

import { SocLevel, WashingSortField } from '@/apps/washing/types/IWashing';
import {
  PersistedWashingFilters,
  persistWashingFilters,
  readPersistedWashingFilters,
  WASHING_PERSISTED_FILTER_KEYS,
} from '@/apps/washing/utils/washingFiltersStorage';
import { currentUserPermissionsChanged } from '@/redux/actions';

// Vehicle data itself lives in the washingApi RTK Query cache (useGetCleaningListQuery),
// not here — this slice only tracks page-local sort/filter UI state.
// `contracts`/`flattenContracts` are ContractDropdown cascader paths;
// `depots` holds leaf paths [contractId, monitoredDepotId] only.
export interface WashingState extends PersistedWashingFilters {
  sortBy: WashingSortField;
  socLevels: SocLevel[];
  parkingQuery: string;
  vehicleIdQuery: string;
}

const defaultState: WashingState = {
  sortBy: 'nextDeparture',
  socLevels: [],
  parkingQuery: '',
  vehicleIdQuery: '',
  contracts: [],
  flattenContracts: [],
  depots: [],
};

const initialState: WashingState = {
  ...defaultState,
  ...readPersistedWashingFilters(),
};

type WashingFiltersPatch = Partial<Omit<WashingState, 'sortBy'>>;

const isNumericId = (value: string | undefined): value is string =>
  value !== undefined && /^\d+$/.test(value);

const samePath = (a: string[], b: string[]) =>
  a.length === b.length && a.every((segment, index) => segment === b[index]);

const persist = (state: WashingState) => {
  const { contracts, flattenContracts, depots } = current(state);
  persistWashingFilters({ contracts, flattenContracts, depots });
};

const clearFilters = (state: WashingState) => {
  state.socLevels = [];
  state.parkingQuery = '';
  state.vehicleIdQuery = '';
  state.contracts = [];
  state.flattenContracts = [];
  state.depots = [];
};

const washingSlice = createSlice({
  name: 'washing',
  initialState,
  reducers: {
    setSortBy(state, action: PayloadAction<WashingSortField>) {
      state.sortBy = action.payload;
    },
    setFilters(state, action: PayloadAction<WashingFiltersPatch>) {
      Object.assign(state, action.payload);
      // The text filters change on every keystroke; only the scope is stored.
      if (WASHING_PERSISTED_FILTER_KEYS.some((key) => key in action.payload)) {
        persist(state);
      }
    },
    setContractFilters(
      state,
      action: PayloadAction<{
        contracts: string[][];
        flattenContracts: string[][];
      }>
    ) {
      const { contracts, flattenContracts } = action.payload;
      state.contracts = contracts;
      state.flattenContracts = flattenContracts;
      // A depot of a contract that is no longer selected would AND the two
      // params into an empty (or forbidden) request. With no contract
      // selected every granted depot is in scope, so the depots stay.
      const contractIds = new Set(
        flattenContracts.map((path) => last(path)).filter(isNumericId)
      );
      if (contractIds.size > 0) {
        state.depots = state.depots.filter(
          (path) => path[0] !== undefined && contractIds.has(path[0])
        );
      }
      persist(state);
    },
    resetFilters(state) {
      clearFilters(state);
      persist(state);
    },
    // Runs once the permissions are loaded: a persisted selection may point
    // at a contract the user has lost since the last visit.
    initializeFiltersFromStorage(
      state,
      action: PayloadAction<{ userContracts: number[] }>
    ) {
      const granted = new Set(action.payload.userContracts.map(String));
      const isGrantedLeaf = (path: string[]) => {
        const leaf = last(path);
        return isNumericId(leaf) && granted.has(leaf);
      };

      const flattenContracts = state.flattenContracts.filter(isGrantedLeaf);
      // A parent path (country/region) stays while a granted leaf beneath it
      // is still selected.
      state.contracts = state.contracts.filter((path) => {
        if (path.length === 0) {
          return false;
        }
        if (isNumericId(last(path))) {
          return isGrantedLeaf(path);
        }
        return flattenContracts.some(
          (flatPath) =>
            flatPath.length >= path.length &&
            samePath(flatPath.slice(0, path.length), path)
        );
      });
      state.flattenContracts = flattenContracts;
      state.depots = state.depots.filter(
        (path) => path[0] !== undefined && granted.has(path[0])
      );
      persist(state);
    },
  },
  // A change to the signed-in user's own contracts can take away the contract
  // a filter points at, so the filters start over rather than silently
  // narrowing the list to something the user can no longer see.
  extraReducers: (builder) => {
    builder.addCase(currentUserPermissionsChanged, (state) => {
      clearFilters(state);
      persist(state);
    });
  },
});

export const {
  setSortBy,
  setFilters,
  setContractFilters,
  resetFilters,
  initializeFiltersFromStorage,
} = washingSlice.actions;
export default washingSlice.reducer;
